import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentsTable = ({ documents, onEdit, onView, onDelete }) => {
  const { t } = useLanguage();
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('expiry');

  const categoryOptions = [
    { value: 'all', label: t('allCategories') },
    { value: 'rams', label: 'H&S Docs' },
    { value: 'insurance', label: t('insurance') },
    { value: 'training', label: t('trainingCertificates') },
    { value: 'plant', label: t('plantCertificates') }
  ];

  const statusOptions = [
    { value: 'all', label: t('allStatus') },
    { value: 'valid', label: t('valid') },
    { value: 'expiring', label: t('expiringSoon') },
    { value: 'expired', label: t('expired') }
  ];

  const sortOptions = [
    { value: 'expiry', label: t('expiryDate') },
    { value: 'title', label: t('title') },
    { value: 'category', label: t('category') }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-xs font-medium">
            <Icon name="CheckCircle2" size={14} />
            {t('valid')}
          </span>
        );
      case 'expiring':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-md text-xs font-medium">
            <Icon name="AlertTriangle" size={14} />
            {t('expiringSoon')}
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-error/10 text-error rounded-md text-xs font-medium">
            <Icon name="AlertCircle" size={14} />
            {t('expired')}
          </span>
        );
      default:
        return null;
    }
  };

  const filteredDocuments = documents?.filter(doc => filterCategory === 'all' || doc?.category === filterCategory)?.filter(doc => filterStatus === 'all' || doc?.status === filterStatus)?.sort((a, b) => {
      if (sortBy === 'expiry') {
        return new Date(a.expiryDate) - new Date(b.expiryDate);
      } else if (sortBy === 'title') {
        return a?.title?.localeCompare(b?.title);
      } else {
        return a?.category?.localeCompare(b?.category);
      }
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Select
          options={categoryOptions}
          value={filterCategory}
          onChange={setFilterCategory}
          placeholder={t('filterByCategory')}
          className="flex-1"
        />
        <Select
          options={statusOptions}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder={t('filterByStatus')}
          className="flex-1"
        />
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          placeholder={t('sortBy')}
          className="flex-1"
        />
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-muted">
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('title')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('category')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('owner')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('issueDate')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('expiryDate')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('status')}</th>
              <th className="text-right p-3 text-sm font-semibold text-foreground">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocuments?.map((doc) => (
              <tr key={doc?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={18} className="text-muted-foreground" />
                    <span className="font-medium text-foreground">{doc?.title}</span>
                  </div>
                </td>
                <td className="p-3 text-sm text-muted-foreground">{doc?.categoryLabel}</td>
                <td className="p-3 text-sm text-muted-foreground">{doc?.owner}</td>
                <td className="p-3 text-sm text-muted-foreground">{doc?.issueDate}</td>
                <td className="p-3 text-sm text-muted-foreground">{doc?.expiryDate}</td>
                <td className="p-3">{getStatusBadge(doc?.status)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(doc?.id)}
                      aria-label={t('view')}
                      title={t('view')}
                    >
                      <Icon name="Eye" size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(doc?.id)}
                      aria-label={t('edit')}
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(doc?.id)}
                      aria-label={t('delete')}
                      title={t('delete')}
                      className="text-destructive hover:text-destructive"
                    >
                      <Icon name="Trash2" size={18} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {filteredDocuments?.map((doc) => (
          <div key={doc?.id} className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <Icon name="FileText" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h4 className="font-medium text-foreground mb-1">{doc?.title}</h4>
                  <p className="text-sm text-muted-foreground">{doc?.categoryLabel}</p>
                </div>
              </div>
              {getStatusBadge(doc?.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground font-caption">{t('owner')}</span>
                <p className="text-foreground font-medium">{doc?.owner}</p>
              </div>
              <div>
                <span className="text-muted-foreground font-caption">{t('issueDate')}</span>
                <p className="text-foreground font-medium">{doc?.issueDate}</p>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground font-caption">{t('expiryDate')}</span>
                <p className="text-foreground font-medium">{doc?.expiryDate}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(doc?.id)}
                iconName="Eye"
                iconPosition="left"
                className="flex-1"
              >
                {t('view')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(doc?.id)}
                iconName="Edit"
                iconPosition="left"
                className="flex-1"
              >
                {t('edit')}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(doc?.id)}
                aria-label={t('delete')}
                title={t('delete')}
                className="text-destructive hover:text-destructive"
              >
                <Icon name="Trash2" size={18} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredDocuments?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-muted rounded-lg">
          <Icon name="FileX" size={48} className="text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-center">{t('noDocumentsMatchFilters')}</p>
        </div>
      )}
    </div>
  );
};

export default DocumentsTable;